import { helpApi } from '@/services/api/help'
import { talkProbe } from './talkProbe'

const cache = new Map<string, string>()
const MAX_CACHE = 8
let shared: HTMLAudioElement | null = null
let playingId: string | null = null
let playCtx: AudioContext | null = null
let ctxSource: AudioBufferSourceNode | null = null
let mouthTimeoutId = 0
let playToken = 0

type AudioSessionNav = Navigator & { audioSession?: { type: string } }

function audioCtor(): typeof AudioContext | undefined {
  const w = window as Window & { webkitAudioContext?: typeof AudioContext }
  return window.AudioContext || w.webkitAudioContext
}

const SILENT_WAV = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA='

export function unlockTalkPlayback(): void {
  try {
    const player = getPlayer()
    player.src = SILENT_WAV
    const started = player.play()
    if (started) void started.then(() => player.pause()).catch(() => {})
  } catch {
    /* no audio element yet */
  }
}

export function primeTalkPlayback(): void {
  const AC = audioCtor()
  if (AC && !playCtx) playCtx = new AC()
  void playCtx?.resume().catch(() => {})
}

function setAudioSession(type: string): void {
  const nav = navigator as AudioSessionNav
  try {
    if (nav.audioSession) nav.audioSession.type = type
  } catch {
    /* ignore */
  }
}

export function routeTalkMic(): void {
  setAudioSession('play-and-record')
}

export function routeTalkSpeaker(): void {
  setAudioSession('playback')
  void playCtx?.resume().catch(() => {})
}

export function releaseTalkMic(): void {
  setAudioSession('auto')
}

function getPlayer(): HTMLAudioElement {
  if (!shared) {
    shared = new Audio()
    ;(shared as HTMLAudioElement & { playsInline: boolean }).playsInline = true
    shared.setAttribute('playsinline', '')
    shared.setAttribute('webkit-playsinline', '')
    shared.preload = 'auto'
  }
  shared.muted = false
  shared.volume = 1
  return shared
}

function remember(key: string, url: string): void {
  if (cache.has(key)) return
  while (cache.size >= MAX_CACHE) {
    const oldest = cache.keys().next().value
    if (oldest == null) break
    const stale = cache.get(oldest)
    if (stale) URL.revokeObjectURL(stale)
    cache.delete(oldest)
  }
  cache.set(key, url)
}

export function revokeHelpAudioCache(): void {
  for (const url of cache.values()) URL.revokeObjectURL(url)
  cache.clear()
}

function audioCacheKey(conversationId: string, messageId: string, part?: string): string {
  return `${conversationId}:${messageId}:${part || ''}`
}

function forgetAudio(key: string): void {
  const stale = cache.get(key)
  if (!stale) return
  URL.revokeObjectURL(stale)
  cache.delete(key)
}

export function dropHelpAudioCache(conversationId: string, messageId: string): void {
  for (const part of ['', 'more']) forgetAudio(audioCacheKey(conversationId, messageId, part))
}

export async function fetchHelpAudioUrl(conversationId: string, messageId: string, part?: string): Promise<string | null> {
  const key = audioCacheKey(conversationId, messageId, part)
  const hit = cache.get(key)
  if (hit) return hit
  try {
    const blob = await helpApi.getAudioBlob(conversationId, messageId, part)
    const url = URL.createObjectURL(blob)
    remember(key, url)
    return url
  } catch {
    return null
  }
}

function stopCtx(): void {
  const src = ctxSource
  ctxSource = null
  if (!src) return
  src.onended = null
  try { src.stop() } catch { /* already stopped */ }
}

function busy(): boolean {
  return !!ctxSource || (!!playingId && !!shared && !shared.paused)
}

function clearMouthTimeout(): void {
  if (!mouthTimeoutId) return
  window.clearTimeout(mouthTimeoutId)
  mouthTimeoutId = 0
}

function armMouthTimeout(
  player: HTMLAudioElement,
  token: number,
  finish: () => void,
): void {
  clearMouthTimeout()
  const known = Number.isFinite(player.duration) && player.duration > 0
  const wait = known ? Math.round(player.duration * 1000) + 500 : 120000
  mouthTimeoutId = window.setTimeout(() => {
    mouthTimeoutId = 0
    if (playToken !== token) return
    if (!player.paused && !player.ended) {
      armMouthTimeout(player, token, finish)
      return
    }
    finish()
  }, wait)
}

function playElement(id: string, url: string, onEnded?: () => void, onStarted?: () => void): void {
  const player = getPlayer()
  const token = ++playToken
  clearMouthTimeout()
  player.pause()
  player.src = url
  playingId = id
  let opened = false
  const finish = () => {
    if (playToken !== token) return
    playToken += 1
    clearMouthTimeout()
    playingId = null
    player.onended = null
    onEnded?.()
  }
  const openMouth = () => {
    if (playToken !== token || opened) return
    opened = true
    onStarted?.()
    armMouthTimeout(player, token, finish)
  }
  player.onended = () => finish()
  const started = player.play()
  if (!started) {
    openMouth()
    return
  }
  void started.then(() => openMouth()).catch(() => {
    routeTalkSpeaker()
    void player.play().then(() => openMouth()).catch(() => {
      talkProbe('tts-fail')
      finish()
    })
  })
}

export function playTalkUrl(id: string, url: string, onEnded?: () => void, onStarted?: () => void): void {
  routeTalkSpeaker()
  if (playingId === id && busy()) {
    stopCtx()
    clearMouthTimeout()
    getPlayer().pause()
    playingId = null
    onEnded?.()
    return
  }
  stopCtx()
  playElement(id, url, onEnded, onStarted)
}

export function isTalkPlaying(id: string): boolean {
  return playingId === id && busy()
}

const ttsQueue: string[] = []
let ttsIdle: (() => void) | undefined
let ttsStarted: (() => void) | undefined

export function enqueueTalkTts(id: string, url: string, onIdle?: () => void, onStarted?: () => void): void {
  ttsIdle = onIdle
  ttsStarted = onStarted
  ttsQueue.push(`${id}\0${url}`)
  pumpTts()
}

function pumpTts(): void {
  getPlayer()
  if (busy()) return
  const next = ttsQueue.shift()
  if (!next) {
    ttsIdle?.()
    return
  }
  const sep = next.indexOf('\0')
  playTalkUrl(next.slice(0, sep), next.slice(sep + 1), () => pumpTts(), ttsStarted)
}

export function stopTalkAudio(): void {
  ttsQueue.length = 0
  ttsIdle = undefined
  ttsStarted = undefined
  clearMouthTimeout()
  playToken += 1
  stopCtx()
  if (shared) {
    shared.pause()
    shared.onended = null
  }
  playingId = null
}

export function stopTalkAudioIfSrc(url: string | null | undefined): void {
  if (!url || !shared) return
  if (shared.src === url) stopTalkAudio()
}
